// ============================================
// Mock battle states for /widget/battles?demo=<name> — lets the streamer
// position the OBS source (and anyone check the look) without a live battle.
// Same shape as GET /api/battles/live. Timestamps are "now", so each state
// plays its fresh-event animation when it loads; the widget reloads it every
// 9s. Slot art comes from the site catalog by name (no image in the mock).
//
// lobby · lobby-arena · lobby-versus · roll · pool-board · bracket · picking ·
// fight · result · final · final-result · third-board · third-place · arena ·
// arena-1v1 · arena-result · arena-2v2-result · versus · versus-result ·
// versus-2v2 · versus-3v3-result · podium · summary · standby · standby-empty
// ============================================

const SLOTS = [
  ['Gates of Olympus 1000', 'Pragmatic Play'], ['Sweet Bonanza 1000', 'Pragmatic Play'], ['Le Bandit', 'Hacksaw Gaming'],
  ['Wanted Dead or a Wild', 'Hacksaw Gaming'], ['Sugar Rush 1000', 'Pragmatic Play'], ['Mental', 'Nolimit City'],
  ['RIP City', 'Hacksaw Gaming'], ['Fruit Party', 'Pragmatic Play'], ['Starlight Princess 1000', 'Pragmatic Play'],
  ['Duel at Dawn', 'Hacksaw Gaming'], ['Big Bass Splash', 'Pragmatic Play'], ['Zeus vs Hades - Gods of War', 'Pragmatic Play'],
  ['Fire in the Hole 3', 'Nolimit City'], ['Densho', 'Hacksaw Gaming'], ['Toshi Video Club', 'Hacksaw Gaming'],
  ['Hand of Anubis', 'Hacksaw Gaming'], ['Chaos Crew 2', 'Hacksaw Gaming'], ['FRKN Bananas', 'Hacksaw Gaming'], ['San Quentin', 'Nolimit City'],
];
const NAMES = ['notpa1n', 'riloded', 'bonushunter', 'jaelawtv', 'reelrunner', 'maxwin_mike', 'NoLimitNate', 'scatterqueen',
  'Slotzilla', 'lowroller', 'xX_Degen_Xx', 'ChatGamba', 'multi_mo', 'spinzilla', 'PixelPusher', 'BuyFeature'];

// deterministic so a demo looks the same every load
let seed = 7;
const rnd = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
const pickMulti = () => { const r = rnd(); return r < 0.2 ? 0 : r < 0.6 ? +(rnd() * 3).toFixed(2) : r < 0.9 ? +(3 + rnd() * 60).toFixed(2) : +(80 + rnd() * 900).toFixed(2); };

const slot = (i) => {
  if (i === 5) return { key: null, name: null, provider: null, image: null, raw: 'that new hacksaw one' };
  const [name, provider] = SLOTS[i % SLOTS.length];
  return { key: `demo-${i}`, name, provider, image: null, raw: name.toLowerCase() };
};
const ago = (ms) => new Date(Date.now() - ms).toISOString();
const name = (i) => `${NAMES[i % NAMES.length]}${i >= NAMES.length ? i : ''}`;

function base(format, extra = {}) {
  return {
    id: 999, format, status: 'live', command: '!join', entries_open: true, title: null, default_buy: 200,
    bracket_size: null, repick: 'keep', prizes: [], current_round: 0, picking_round: null, streamer_name: 'Snutzy',
    focus_match_id: null, created_at: ago(600000), started_at: ago(500000), finished_at: null, ...extra,
  };
}
const entries = (n) => Array.from({ length: n }, (_, i) => ({
  id: i + 1, name: name(i), slot: slot(i), rolled: false, entered_at: ago((n - i) * 7000), updated_at: ago(i * 2500),
}));
const wrap = (battle, rest) => ({
  battle, entry_count: rest.entry_count ?? rest.entries?.length ?? 0, entries: rest.entries || [], players: rest.players || [],
  matches: rest.matches || [], server_time: new Date().toISOString(),
});
const play = (id, side, ord, p, res, at) => ({
  id, side, ord, player_id: p?.id ?? null, name: p?.name ?? 'Snutzy', is_streamer: !p, handpicked: !!p?.handpicked, slot: p?.slot ?? slot(ord + 11),
  buy: res == null ? null : 200, payout: res == null ? null : +(res * 200).toFixed(2), multi: res, result_at: res == null ? null : at,
});

// A tournament of `size`, with `done` matches decided in `round` (rounds
// before it complete). Returns [state, currentRoundMatches].
function tournament(size, { round = 1, done = 0, picking = false, finished = false, freshLast = false, freshFinal = false, rolled = null, freshRoll = false } = {}) {
  seed = 11;
  const players = Array.from({ length: size }, (_, i) => ({
    id: i + 1, name: name(i), seat: i, team: null, match_id: null, slot: slot(i), picked_round: 1,
    eliminated_round: null, placement: null, prize_amount: null, awarded: false, replaced: false, rolled_at: ago(500000),
  }));
  const matches = [];
  let playId = 1;
  let feeders = players.slice();
  const rounds = Math.log2(size);
  for (let r = 1; r <= rounds; r++) {
    const next = [];
    for (let i = 0; i < size / 2 ** r; i++) {
      const a = feeders[2 * i];
      const b = feeders[2 * i + 1];
      const decided = finished || r < round || (r === round && i < done);
      let ma = null;
      let mb = null;
      if (decided) { ma = pickMulti(); mb = pickMulti(); if (ma === mb) mb += 0.5; }
      const fresh = freshLast && r === round && i === done - 1;
      const at = fresh ? ago(1500) : ago(300000);
      const unrolled = r === 1 && rolled != null && i >= rolled;
      const justRolled = freshRoll && r === 1 && i === rolled - 1;
      const m = {
        id: 100 + matches.length, round: r, idx: i, team_size: 1, status: decided ? 'done' : 'pending',
        winner_side: decided ? (ma > mb ? 'a' : 'b') : null, a_score: ma, b_score: mb, forfeit: false, prize_amount: null,
        created_at: ago(500000), decided_at: decided ? at : null,
        rolled_at: r !== 1 || unrolled ? null : justRolled ? ago(200) : ago(400000),
        plays: unrolled ? [] : [a && play(playId++, 'a', 0, a, ma, at), b && play(playId++, 'b', 0, b, mb, at)].filter(Boolean),
      };
      matches.push(m);
      if (decided) {
        const [w, l] = ma > mb ? [a, b] : [b, a];
        l.eliminated_round = r;
        next.push(w);
      } else next.push(null);
    }
    feeders = next;
  }
  const battle = base('tournament', {
    bracket_size: size, prizes: [500, 250, 100], current_round: round, entries_open: false,
    repick: 'each_round', picking_round: picking ? round : null, title: 'Friday Cup',
  });
  if (picking) players.filter((p) => p.eliminated_round == null).forEach((p, i) => { p.picked_round = i % 3 === 0 ? 1 : round; p.slot = slot(i + 7); });
  if (finished) {
    battle.status = 'finished';
    battle.finished_at = ago(4000);
    const final = matches[matches.length - 1];
    const champ = players.find((p) => p.eliminated_round == null);
    const second = players.find((p) => p.eliminated_round === rounds);
    const semis = players.filter((p) => p.eliminated_round === rounds - 1);
    [champ, second, ...semis].forEach((p, i) => { p.placement = i + 1; p.prize_amount = [500, 250, 100][i] ?? null; });
    final.decided_at = freshFinal ? ago(600) : ago(20000);
    if (freshFinal) final.plays.forEach((p) => { p.result_at = final.decided_at; });
  }
  if (rolled != null) {
    battle.entries_open = true;
    const rolledIds = new Set(matches.filter((m) => m.round === 1 && m.plays.length).flatMap((m) => m.plays.map((p) => p.player_id)));
    players.splice(0, players.length, ...players.filter((p) => rolledIds.has(p.id)));
  }
  return [wrap(battle, { entries: entries(24), entry_count: 37, players, matches }), matches.filter((m) => m.round === round)];
}

// Turns a tournament state into a 3rd-place one: the semi-final losers get
// their match beside the final (round = last, idx 1). Returns that match.
function addThirdPlace(s) {
  s.battle.third_place = true;
  const rounds = Math.log2(s.battle.bracket_size);
  const losers = s.matches.filter((m) => m.round === rounds - 1 && m.status === 'done')
    .map((m) => m.plays.find((p) => p.side !== m.winner_side));
  const m = {
    id: 900, round: rounds, idx: 1, team_size: 1, status: 'pending', winner_side: null, a_score: null, b_score: null,
    forfeit: false, prize_amount: null, created_at: ago(500000), decided_at: null, rolled_at: null,
    plays: losers.map((p, i) => ({ ...p, id: 9000 + i, side: i ? 'b' : 'a', buy: null, payout: null, multi: null, result_at: null })),
  };
  s.matches.push(m);
  return m;
}

function fight(format, teamSize, { decided = false, rolledJustNow = true, prize = 150 } = {}) {
  seed = 3;
  const versus = format === 'versus';
  // versus: the streamer's handpicked teammates (side a) + the rolled chat side
  const mates = versus ? teamSize - 1 : 0;
  const n = versus ? mates + teamSize : teamSize * 2;
  const players = Array.from({ length: n }, (_, i) => ({
    id: i + 1, name: name(i + 2), seat: null,
    team: versus ? (i < mates ? 'a' : 'b') : i % 2 ? 'b' : 'a',
    handpicked: versus && i < mates,
    match_id: 50, slot: slot(versus ? i + 2 : i + 6),
    picked_round: null, eliminated_round: null, placement: null, prize_amount: null, awarded: false, replaced: false, rolled_at: ago(2000),
  }));
  const res = (i) => (decided ? [2.4, 0.35, 11.8, 64.2, 1.1, 5.5][i % 6] : null);
  const at = ago(1200);
  let plays;
  if (versus && teamSize === 1) {
    plays = [play(1, 'a', 0, null, decided ? 18.6 : null, ago(30000)), play(2, 'b', 0, players[0], decided ? 42.25 : null, at)];
  } else if (versus) {
    const chat = players.filter((p) => p.team === 'b');
    plays = [
      play(1, 'a', 0, null, decided ? 18.6 : null, ago(30000)),
      ...players.filter((p) => p.team === 'a').map((p, i) => play(2 + i, 'a', i + 1, p, res(i), ago(30000))),
      ...chat.map((p, i) => play(10 + i, 'b', i, p, res(i + 3), i === chat.length - 1 ? at : ago(30000))),
    ];
  } else {
    // earlier bonuses already in; only the last one lands now (like live)
    plays = players.map((p, i) => play(i + 1, p.team, Math.floor(i / 2), p, res(i), i === players.length - 1 ? at : ago(30000)));
  }
  const score = (side) => { const xs = plays.filter((p) => p.side === side).map((p) => p.multi); return xs.reduce((s, x) => s + x, 0) / xs.length; };
  const m = {
    id: 50, round: 3, idx: 0, team_size: teamSize, status: decided ? 'done' : 'pending',
    winner_side: decided ? (score('a') > score('b') ? 'a' : 'b') : null, a_score: decided ? score('a') : null,
    b_score: decided ? score('b') : null, forfeit: false, prize_amount: prize,
    created_at: rolledJustNow ? ago(300) : ago(60000), decided_at: decided ? at : null, plays,
    rolled_at: rolledJustNow ? ago(300) : ago(60000),
  };
  const past = [1, 2].map((r) => ({
    id: 40 + r, round: r, idx: 0, team_size: 1, status: 'done', winner_side: r === 1 ? 'a' : 'b', a_score: 3, b_score: 1,
    forfeit: false, prize_amount: 100, created_at: ago(900000), decided_at: ago(800000), plays: [],
  }));
  // a versus house win pays nobody (teammates included)
  if (decided && !(format === 'versus' && m.winner_side === 'a')) {
    const winners = players.filter((p) => p.team === m.winner_side);
    winners.forEach((p) => { p.prize_amount = prize / winners.length; });
  }
  const battle = base(format, { focus_match_id: 50 });
  return wrap(battle, { entries: entries(30), entry_count: 58, players, matches: [...past, m] });
}

export function demoState(which) {
  switch (which) {
    case 'lobby': {
      const b = base('tournament', { status: 'lobby', bracket_size: 16, prizes: [500, 250, 100], title: 'Friday Cup' });
      return wrap(b, { entries: entries(11) });
    }
    case 'lobby-arena': return wrap(base('arena', { status: 'lobby', default_team_size: 3, default_prize: 300 }), { entries: entries(9) });
    case 'lobby-versus': return wrap(base('versus', { status: 'lobby', default_team_size: 1, default_prize: 100 }), { entries: entries(6) });
    case 'roll': {
      const [s, cur] = tournament(16, { round: 1, done: 3, rolled: 4, freshRoll: true });
      s.battle.focus_match_id = cur[3].id;
      return s;
    }
    case 'pool-board': return tournament(16, { round: 1, done: 3, rolled: 5 })[0];
    case 'bracket': return tournament(16, { round: 2, done: 2 })[0];
    case 'picking': return tournament(16, { round: 2, done: 0, picking: true })[0];
    case 'fight': {
      const [s, cur] = tournament(16, { round: 3, done: 0 });
      const m = cur[0];
      m.plays[0] = { ...m.plays[0], buy: 200, payout: 3710, multi: 18.55, result_at: new Date(Date.now() - 800).toISOString() };
      s.battle.focus_match_id = m.id;
      return s;
    }
    case 'result': {
      const [s, cur] = tournament(16, { round: 3, done: 1, freshLast: true });
      s.battle.focus_match_id = cur[0].id;
      return s;
    }
    case 'final': {
      const [s, cur] = tournament(8, { round: 3, done: 0 });
      s.battle.focus_match_id = cur[0].id;
      return s;
    }
    case 'final-result': return tournament(8, { finished: true, freshFinal: true })[0];
    case 'third-board': {
      const [s] = tournament(8, { round: 3, done: 0 });
      addThirdPlace(s);
      return s;
    }
    case 'third-place': {
      const [s] = tournament(8, { round: 3, done: 0 });
      s.battle.focus_match_id = addThirdPlace(s).id;
      return s;
    }
    case 'arena': return fight('arena', 3);
    case 'arena-result': return fight('arena', 3, { decided: true, rolledJustNow: false, prize: 300 });
    case 'arena-2v2-result': return fight('arena', 2, { decided: true, rolledJustNow: false, prize: 200 });
    case 'arena-1v1': return fight('arena', 1);
    case 'versus': return fight('versus', 1);
    case 'versus-result': return fight('versus', 1, { decided: true, rolledJustNow: false, prize: 100 });
    case 'versus-2v2': return fight('versus', 2);
    case 'versus-3v3-result': return fight('versus', 3, { decided: true, rolledJustNow: false, prize: 300 });
    case 'podium': return tournament(16, { finished: true })[0];
    case 'summary': {
      const s = fight('versus', 1, { decided: true, rolledJustNow: false });
      s.battle.status = 'finished';
      s.battle.finished_at = ago(5000);
      return s;
    }
    case 'standby': {
      // a tournament that finished an hour ago: stand-by with its recap line
      const [s] = tournament(16, { finished: true });
      s.battle.finished_at = ago(3600000);
      return s;
    }
    case 'standby-empty': return null;
    default: return demoState('lobby');
  }
}
